export interface IEventEmitter {
    on(event: string, listener: IListener, options?: any): ICancelListener;
    on(events: string[], listener: IListener, options?: any): ICancelListener;
    once?(event: string, listener: IListener): ICancelListener;
    emit(event: string, parameter?: any): void;
}

export interface IListener {
    (args?: any, eventOptions?: any): any;
}

export interface ICancelListener {
    (): void;
}

export class EventEmitter implements IEventEmitter {
    private _listeners: Map<string, IListener[]>;
    protected _debug: boolean;
    protected _debugUuid: string;

    constructor() {
        this._listeners = new Map();
        this._debug = false;
    }

    public setDebug(value: boolean, uuid = 'EventEmitter') {
        this._debug = value;
        this._debugUuid = uuid;
    }

    /**
     * Gets all of the event names that are subscribed to.
     * @returns event names
     */
    public getEventKeys(): string[] {
        return Array.from(this._listeners.keys());
    }

    public on(event: string | string[], listener: IListener, options?: any): ICancelListener {
        const events: string[] = Array.isArray(event) ? event : [event];
        events.forEach(event => {
            if (this._debug) {
                // eslint-disable-next-line no-console
                console.log(`${this._debugUuid}::on(${event})`, listener, options);
            }
            if (this._listeners.has(event)) {
                const listeners = this._listeners.get(event);
                if (listeners && listeners.indexOf(listener) === -1) {
                    if (options && options['addBefore']) {
                        listeners.unshift(listener);
                    } else {
                        listeners.push(listener);
                    }
                }
            } else {
                this._listeners.set(event, [listener]);
            }
        });
        return () => {
            events.forEach(event => {
                this.removeListener(event, listener);
            });
        };
    }

    /**
     * Emits an event with data once and cleans up event emitter
     * @param event to execute and remove
     * @param listener to execute
     * @returns cancelled event function
     */
    public once(event: string, listener: IListener): ICancelListener {
        if (this._debug) {
            // eslint-disable-next-line no-console
            console.log(`${this._debugUuid}::once(${event})`, listener);
        }
        let off: ICancelListener = undefined;
        const wrapper = function (parameter: any) {
            listener(parameter);
            off();
        };
        off = this.on(event, wrapper);
        return off;
    }

    /**
     * Removes an event listener from the list of listeners.
     * @param event to remove
     * @param listener function to remove
     * @returns void
     */
    public removeListener(event: string, listener: IListener): void {
        if (!this._listeners.has(event)) return;

        const listeners = this._listeners.get(event);
        if (listeners && listeners.length) {
            for (let i = 0; i < listeners.length; i++) {
                if (listeners[i] === listener) {
                    listeners.splice(i, 1);
                    if (this._debug) {
                        // eslint-disable-next-line no-console
                        console.log(`${this._debugUuid}::remove(${event})`, listener);
                    }
                    break;
                }
            }
        }
    }

    /**
     * Emits an event with optional data
     * @param event name to emit on
     * @param parameter data to send to the listener
     * @returns void
     */
    public emit(event: string, parameter?: any): void {
        if (this._debug) {
            // eslint-disable-next-line no-console
            console.log(`${this._debugUuid}::emit(${event})`, parameter);
        }
        if (this._listeners.has(event)) {
            const listeners = this._listeners.get(event);
            if (!listeners.length) return;

            const eventOption = {
                stopped: false,
                stop: function () {
                    this.stopped = true;
                }
            };
            for (let i = 0; i < listeners.length; i++) {
                if (!eventOption.stopped && listeners[i]) {
                    listeners[i](parameter, eventOption);
                }
            }
        }
    }

    /**
     * Removes all event listeners.
     */
    public erase() {
        this._listeners.clear();
    }
}
